import { type FC, Fragment, useCallback } from "react";

import {
  Box,
  Button,
  Column,
  DescriptionAddIcon,
  Menu,
  MenuActionsButton,
  MenuItem,
  MenuList,
  PlusIcon,
  Row,
  Text,
} from "@hightouch/htui";
import {
  Controller,
  FormProvider,
  useFormContext,
  useWatch,
} from "react-hook-form";

import type { MinimalParentModel } from "src/components/audiences/types";
import { QueryBuilderProvider } from "src/components/explore/context/query-builder-context";
import {
  AndOrToggleButton,
  XButton,
} from "src/components/explore/visual/condition-buttons";
import { NestedPropertyFilter } from "src/components/explore/visual/nested-property-filter";

import type { FullParentModel } from "src/components/audiences/types";
import {
  ConditionType,
  createPropertyCondition,
  createRootPropertyCondition,
  isPropertyCondition,
} from "src/types/visual";
import type {
  AndOrCondition,
  FilterableColumn,
  PropertyCondition,
} from "src/types/visual";
import { colors } from "src/components/explore/visual/colors";
import { useQueryBuilderContext } from "src/components/explore/context/hooks";
import { FilterableOptionsProvider } from "src/components/explore/filter-popover/filterable-options-context";
import { TRAIT_SUBCONDITION_FILTERABLE_SECTIONS } from "src/components/explore/filter-popover/constants";
import { produce } from "immer";

type MinimalRelationship = {
  to_model: {
    id: string;
    filterable_audience_columns: FilterableColumn[];
  };
};

type TraitConditionsProps = {
  parentModel: FullParentModel;
  relationship: MinimalRelationship | undefined | null;
  isAudienceTrait?: boolean;
  isLoading?: boolean;
  /** Form path the conditions are written to; defaults to the trait's own. */
  conditionsPath?: string;
};

export const TraitConditions: FC<Readonly<TraitConditionsProps>> = ({
  parentModel,
  relationship,
  isAudienceTrait,
  isLoading = false,
  conditionsPath,
}) => {
  const formContext = useFormContext();
  const { setValue, watch, getValues, reset } = formContext;

  const pathToConditions =
    conditionsPath ?? (isAudienceTrait ? "conditions" : "config.conditions");

  const topLevelConditions: AndOrCondition<PropertyCondition>[] =
    watch(pathToConditions) ?? [];

  // Note: There can only be one top-level condition, so we only want to add a top-level condition if there are none present
  if (topLevelConditions.length === 0) {
    return (
      <AddConditionButton
        conditionType={ConditionType.And}
        isDisabled={!relationship}
        onClick={() =>
          setValue(pathToConditions, [createRootPropertyCondition()], {
            shouldDirty: true,
          })
        }
      />
    );
  }

  return (
    <FormProvider {...formContext}>
      <QueryBuilderProvider parentModel={parentModel}>
        <Column>
          <FilterableOptionsProvider
            modelId={relationship?.to_model.id}
            sections={TRAIT_SUBCONDITION_FILTERABLE_SECTIONS}
          >
            {/* Note: there should only be one top-level condition due to the conditional above*/}
            {topLevelConditions.map((condition, index) => (
              <SubConditions
                key={condition.id}
                isLoading={isLoading}
                pathToConditions={`${pathToConditions}.${index}.conditions`}
                superConditionType={
                  condition.type === ConditionType.And ||
                  condition.type === ConditionType.Or
                    ? condition.type
                    : ConditionType.And
                }
                depth={index}
                parentModel={parentModel}
                relationship={relationship}
                onRemove={() => {
                  // If the last sub-condition was just removed, clear the root wrapper entirely
                  const remaining = getValues(
                    `${pathToConditions}.${index}.conditions`,
                  ) as AndOrCondition<PropertyCondition>[] | undefined;

                  if (remaining?.length === 0) {
                    setValue(pathToConditions, [], { shouldDirty: true });
                    // To properly dirty the form, we need to reset the form when every condition is removed.
                    reset(undefined, {
                      keepValues: true,
                      keepDefaultValues: true,
                    });
                  }
                }}
                onToggleWrappingConditionType={() => {
                  setValue(
                    `${pathToConditions}.${index}`,
                    {
                      ...condition,
                      type:
                        condition.type === ConditionType.And
                          ? ConditionType.Or
                          : ConditionType.And,
                    },
                    { shouldDirty: true },
                  );
                }}
              />
            ))}
          </FilterableOptionsProvider>
        </Column>
      </QueryBuilderProvider>
    </FormProvider>
  );
};

type SubConditionsProps = {
  parentModel: MinimalParentModel;
  relationship: MinimalRelationship | undefined | null;
  pathToConditions: string;
  depth: number;
  superConditionType: ConditionType.And | ConditionType.Or;
  isLoading?: boolean;
  onRemove?: () => void;
  onToggleWrappingConditionType: () => void;
  onUngroupCondition?: (propertyConditions: PropertyCondition[]) => void;
};

const SubConditions: FC<Readonly<SubConditionsProps>> = ({
  pathToConditions,
  superConditionType,
  depth,
  parentModel,
  relationship,
  isLoading = false,
  onRemove,
  onToggleWrappingConditionType,
  onUngroupCondition,
}) => {
  const { control, setValue, getValues } = useFormContext();
  const { maxNestingDepth } = useQueryBuilderContext();

  const conditions: AndOrCondition<PropertyCondition>[] = useWatch({
    control,
    name: pathToConditions,
  });

  const isGrouped = onUngroupCondition != null;
  const oppositeCondition =
    superConditionType === ConditionType.And
      ? ConditionType.Or
      : ConditionType.And;

  const updateConditions = useCallback(
    (update: AndOrCondition<PropertyCondition>[]) => {
      setValue(pathToConditions, update, { shouldDirty: true });
    },
    [pathToConditions, setValue],
  );

  return (
    <Column gap={4}>
      <Column gap={4}>
        {conditions?.map((condition, conditionIndex) => {
          if (
            condition.type === ConditionType.And ||
            condition.type === ConditionType.Or
          ) {
            return (
              <Fragment key={condition.id}>
                {conditionIndex > 0 && (
                  <AndOrToggleButton
                    conditionType={superConditionType}
                    mr={0}
                    onClick={onToggleWrappingConditionType}
                  />
                )}
                <Row gap={2}>
                  <Column
                    bg={colors.base[superConditionType]}
                    borderLeftRadius="md"
                    h="100%"
                    w={2}
                    mr={2}
                  />
                  <SubConditions
                    pathToConditions={`${pathToConditions}.${conditionIndex}.conditions`}
                    superConditionType={condition.type}
                    depth={depth + 1}
                    parentModel={parentModel}
                    relationship={relationship}
                    isLoading={isLoading}
                    onRemove={() => {
                      const remaining = getValues(
                        `${pathToConditions}.${conditionIndex}.conditions`,
                      ) as AndOrCondition<PropertyCondition>[] | undefined;
                      if (remaining?.length === 0) {
                        updateConditions(
                          conditions.filter((_, i) => i !== conditionIndex),
                        );
                        // Also need to propagate the removal to the parent condition if it's empty
                        onRemove?.();
                      }
                    }}
                    onToggleWrappingConditionType={() => {
                      updateConditions(
                        conditions.map((c, i) =>
                          i === conditionIndex
                            ? {
                                ...c,
                                type:
                                  c.type === ConditionType.And
                                    ? ConditionType.Or
                                    : ConditionType.And,
                              }
                            : c,
                        ) as AndOrCondition<PropertyCondition>[],
                      );
                    }}
                    onUngroupCondition={(groupedCondition) => {
                      updateConditions([
                        ...conditions.slice(0, conditionIndex),
                        ...groupedCondition, // Flatten the grouped condition
                        ...conditions.slice(conditionIndex + 1),
                      ]);
                    }}
                  />
                </Row>
              </Fragment>
            );
          }

          return (
            <Controller
              key={condition.id}
              name={`${pathToConditions}.${conditionIndex}`}
              control={control}
              render={({ field }) => (
                <Row gap={2}>
                  <NestedPropertyFilter
                    lhsPropertyFilterVariant="dropdown"
                    audience={undefined}
                    isLoading={isLoading}
                    condition={field.value}
                    parent={undefined}
                    isFirstCondition={conditionIndex === 0 && depth === 0}
                    isWrappedWithAndCondition={
                      superConditionType === ConditionType.And
                    }
                    hideAndOrToggleButton={depth > 0 && conditionIndex === 0}
                    onUpdate={(updater) =>
                      field.onChange(produce(getValues(field.name), updater))
                    }
                    onToggleWrappingConditionType={
                      onToggleWrappingConditionType
                    }
                  />
                  <Menu>
                    <MenuActionsButton />
                    <MenuList>
                      {depth < maxNestingDepth && (
                        <MenuItem
                          icon={DescriptionAddIcon}
                          onClick={() => {
                            field.onChange({
                              type: oppositeCondition,
                              conditions: [field.value],
                              id: crypto.randomUUID(),
                            } satisfies AndOrCondition<PropertyCondition>);
                          }}
                        >
                          <Column>
                            <Text fontWeight="medium">
                              Add &ldquo;{oppositeCondition.toUpperCase()}
                              &rdquo; group
                            </Text>
                            <Text color="text.secondary">
                              Create a nested {oppositeCondition.toUpperCase()}{" "}
                              group from the filter
                            </Text>
                          </Column>
                        </MenuItem>
                      )}

                      {isGrouped && (
                        <MenuItem
                          icon={DescriptionAddIcon}
                          onClick={() => {
                            // Filter out PropertyConditions for ungrouping
                            const propertyConditions =
                              conditions.filter(isPropertyCondition);
                            onUngroupCondition?.(propertyConditions);
                          }}
                        >
                          <Column>
                            <Text fontWeight="medium">Ungroup</Text>
                            <Text color="text.secondary">
                              Remove this filter from this nested group
                            </Text>
                          </Column>
                        </MenuItem>
                      )}
                    </MenuList>
                  </Menu>
                  <XButton
                    onRemove={() => {
                      updateConditions(
                        conditions.filter((_, i) => i !== conditionIndex),
                      );
                      onRemove?.();
                    }}
                  />
                </Row>
              )}
            />
          );
        })}
        <AddConditionButton
          conditionType={superConditionType}
          isDisabled={!relationship}
          onClick={() => {
            updateConditions([...conditions, createPropertyCondition()]);
          }}
        />
      </Column>
    </Column>
  );
};

const AddConditionButton: FC<{
  conditionType: ConditionType.And | ConditionType.Or;
  isDisabled: boolean;
  onClick: () => void;
}> = ({ conditionType, isDisabled, onClick }) => (
  <Box
    as={Button}
    icon={PlusIcon}
    isDisabled={isDisabled}
    alignItems="center"
    size="md"
    display="flex"
    width="min-content"
    px={2}
    bg={colors.base[conditionType]}
    border="none"
    _active={{
      bg: colors.hover[conditionType],
    }}
    _hover={{ bg: colors.hover[conditionType] }}
    sx={{
      "&[aria-expanded=true]": {
        boxShadow: "none",
        _focusVisible: {
          boxShadow: "outline",
        },
      },
    }}
    onClick={onClick}
  >
    Add filter
  </Box>
);
